import { useLayoutEffect, useRef, useState, type Ref } from "react"
import { generateHTML, type JSONContent } from "@tiptap/core"
import DOMPurify from "dompurify"

import { Heading } from "@/components/typography"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Skeleton } from "@/components/ui/skeleton"
import { tiptapJsonToPlainText } from "@/shared/tiptapText"
import { richTextExtensions } from "@/utils/richTextExtensions"

export type NotesPreviewCellProps = {
  notes?: Record<string, unknown> | string | null
}

export function NotesPreviewCell({ notes }: NotesPreviewCellProps) {
  const plain = tiptapJsonToPlainText(notes)
  const [open, setOpen] = useState(false)
  const { clampRef, overflows } = useContentOverflow(plain)

  if (!plain || notes == null) return null

  return (
    <div>
      <NotesContent ref={clampRef} notes={notes} className="line-clamp-2 typeset typeset-compact whitespace-normal" />
      {overflows ? (
        <Button
          type="button"
          variant="link"
          size="xs"
          className="h-auto p-0 font-normal"
          onClick={() => setOpen(true)}
        >
          View Full Notes
        </Button>
      ) : null}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden sm:max-w-lg">
          <DialogHeader className="shrink-0">
            <DialogTitle render={<Heading type="h3" tag="h2" />}>Notes</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 overflow-y-auto">
            <NotesContent notes={notes} className="typeset" />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export function NotesPreviewCellSkeleton() {
  return (
    <div className="space-y-1">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-4/5" />
    </div>
  )
}

function NotesContent({ notes, className, ref, }: {
  notes: Record<string, unknown> | string
  className?: string
  ref?: Ref<HTMLDivElement>
}) {
  let html: string | undefined
  if (typeof notes !== "string") {
    try {
      html = DOMPurify.sanitize(generateHTML(notes as JSONContent, richTextExtensions()))
    } catch {
      html = undefined
    }
  }

  if (html == null) {
    return (
      <div ref={ref} className={className}>
        {typeof notes === "string" ? notes : tiptapJsonToPlainText(notes)}
      </div>
    )
  }

  // Only ever use `dangerouslySetInnerHTML` with HTML sanitized via DOMPurify.
  return <div ref={ref} className={className} dangerouslySetInnerHTML={{ __html: html }} />
}

function useContentOverflow(watch: string) {
  const clampRef = useRef<HTMLDivElement>(null)
  const [overflows, setOverflows] = useState(false)

  useLayoutEffect(() => {
    const clamp = clampRef.current
    if (!clamp) return

    const measure = () => {
      setOverflows(clamp.scrollHeight > clamp.clientHeight + 1)
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(clamp)
    let cancelled = false
    document.fonts?.ready.then(() => {
      if (!cancelled) measure()
    })
    return () => {
      cancelled = true
      observer.disconnect()
    }
  }, [watch])

  return { clampRef, overflows }
}
